import { evaluateAt } from './evaluate';
import { type Node } from './parse';

/**
 * Finding the lines a curve approaches but never reaches.
 *
 * ## Why this is worth a module
 *
 * Because for one chapter of the Vietnamese syllabus the asymptotes *are* the figure.
 * `y = (2x+1)/(x−1)` drawn without them is two arcs floating on a page: nothing says where they
 * are going, and "cho đồ thị như hình vẽ, tìm tiệm cận đứng" — a question that appears in the
 * national exam most years — cannot be set from it at all.
 *
 * They can be faked today, with a line through two plotted points and the `faint` style. That is
 * a trick, and a trick is no use to the two audiences this product is for: a teacher who does not
 * know it exists, and a model turning a problem statement into a script, which will not invent it.
 *
 * ## Why numerically, in a codebase that prizes exactness
 *
 * ADR-0003 makes constructions exact, and rightly: a midpoint that is *nearly* the midpoint is a
 * drawing that lies. An asymptote is not that kind of object. It is an annotation *about* a
 * curve, drawn dashed precisely to say "this is the limit, not part of the graph" — and the curve
 * it annotates is itself sampled rather than solved.
 *
 * The gain is reach. Solving rational functions symbolically would cover `(ax+b)/(cx+d)` and stop
 * there; testing limits numerically also finds the vertical asymptotes of `tan x` and `ln x`, and
 * the horizontal one of `2^x` — all of which are on the syllabus and none of which are rational.
 *
 * What is given up is certainty at the edges: a function that grows very slowly can be mistaken
 * for one with a limit. The thresholds below are set so that the mistake is *missing* an
 * asymptote rather than inventing one, because a figure short an annotation is incomplete and a
 * figure with a line the function never approaches is wrong.
 */

export interface Asymptote {
  readonly kind: 'vertical' | 'horizontal' | 'oblique';
  /** `x = at` for a vertical one; `y = at` for a horizontal one. */
  readonly at: number;
  /** `y = slope·x + at`, for an oblique one only. */
  readonly slope?: number;
}

/** Far enough out that a limit has settled, close enough that a double still holds it. */
const FAR = 1e6;

/** Two probes an order of magnitude apart. A real limit barely moves between them. */
const FARTHER = 1e7;

/** How closely the two probes must agree before a value counts as a limit. */
const LIMIT_TOLERANCE = 1e-4;

/** Below this a curve has not run away, whatever the samples suggest. */
const RUNAWAY = 1e4;

/** How near a suspected pole to probe from. */
const APPROACH = [1e-3, 1e-4, 1e-5] as const;

/**
 * Rounds a limit that is obviously a round number.
 *
 * A numeric limit comes back as `1.9999999998`, and an axis labelled `1.9999999998` is worse than
 * no axis. Snapping only when the value is already within a whisker keeps `y = 2` exact and
 * leaves a genuinely awkward limit alone.
 */
function tidy(value: number): number {
  const near = Math.round(value * 1000) / 1000;
  return Math.abs(near - value) < 1e-6 ? near : value;
}

/** The value a function settles on far out, in one direction, or `null` if it does not settle. */
function limitAt(node: Node, sign: 1 | -1): number | null {
  const near = evaluateAt(node, sign * FAR);
  const far = evaluateAt(node, sign * FARTHER);
  if (near === null || far === null) return null;

  // Relative, so a limit of 1000 is not held to the same absolute closeness as a limit of 0.001.
  const scale = Math.max(1, Math.abs(near));
  return Math.abs(near - far) / scale < LIMIT_TOLERANCE ? tidy(far) : null;
}

/**
 * Whether the curve genuinely escapes at `x`, tested from whichever side it exists on.
 *
 * A break in the sampled curve is only a *candidate*. `sqrt(x)` breaks at 0 and has no asymptote
 * there; `abs(x)/x` breaks at 0 and is bounded on both sides. What distinguishes a pole is that
 * the values keep growing as the probe closes in, so that is what is measured rather than assumed.
 */
function escapesAt(node: Node, x: number): boolean {
  for (const side of [1, -1] as const) {
    let previous = 0;
    let growing = true;
    let reached = 0;

    for (const distance of APPROACH) {
      const value = evaluateAt(node, x + side * distance);
      if (value === null) {
        growing = false;
        break;
      }
      const size = Math.abs(value);
      if (size < previous) growing = false;
      previous = size;
      reached = size;
    }

    if (growing && reached > RUNAWAY) return true;
  }
  return false;
}

/**
 * Candidate poles, found two different ways because a pole shows itself two different ways.
 *
 * The first version looked only for the function becoming *undefined*, and it was wrong twice
 * over.
 *
 * It missed `tan x` entirely: JavaScript's `Math.tan(Math.PI/2)` is not infinity, it is
 * 1.6 × 10¹⁶ — a perfectly finite number — so nothing was ever undefined and no pole was ever
 * suspected. Sixty-four asymptotes, all invisible to the test.
 *
 * And it found `1/x` only by luck. Over `[−5, 5]` with four hundred probes one of them lands
 * exactly on zero, so the function *was* undefined at a probe. Move the interval to `[−5, 6]`
 * and the same pole disappears. A detector that depends on a pole landing on a sample is a
 * detector that works on the examples it was written against.
 *
 * So both signatures are looked for: a definedness boundary, and a **sign change across a large
 * value**, which is what a pole looks like when the arithmetic merely overflows instead of
 * failing. The second is located by bisecting `1/f`, which passes smoothly through zero exactly
 * where `f` has its pole — the one place the reciprocal is better behaved than the function.
 */

/** A candidate must tower over the ordinary scale of the curve, not merely be large. */
const POLE_FACTOR = 50;

function median(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] ?? 0;
}

/** Bisects on definedness: the boundary of a region the function does not reach. */
function narrowBoundary(node: Node, lo: number, hi: number, definedAtLo: boolean): number {
  let low = lo;
  let high = hi;
  for (let step = 0; step < 60; step += 1) {
    const mid = (low + high) / 2;
    if ((evaluateAt(node, mid) !== null) === definedAtLo) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

/** Bisects `1/f` for its zero, which sits exactly on the pole. */
function narrowPole(node: Node, lo: number, hi: number): number {
  const reciprocal = (x: number): number => {
    const value = evaluateAt(node, x);
    return value === null || value === 0 ? 0 : 1 / value;
  };

  let low = lo;
  let high = hi;
  const atLow = reciprocal(low);
  for (let step = 0; step < 60; step += 1) {
    const mid = (low + high) / 2;
    if (reciprocal(mid) * atLow > 0) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

function suspectedPoles(node: Node, from: number, to: number, probes: number): number[] {
  const step = (to - from) / probes;
  const xs: number[] = [];
  const ys: (number | null)[] = [];

  for (let index = 0; index <= probes; index += 1) {
    const x = from + step * index;
    xs.push(x);
    ys.push(evaluateAt(node, x));
  }

  const sizes = ys.filter((y): y is number => y !== null).map(Math.abs);
  const ordinary = Math.max(median(sizes), 1);
  const towering = ordinary * POLE_FACTOR;

  const found: number[] = [];
  for (let index = 1; index <= probes; index += 1) {
    const before = ys[index - 1] ?? null;
    const now = ys[index] ?? null;
    const lo = xs[index - 1] ?? from;
    const hi = xs[index] ?? to;

    if ((before === null) !== (now === null)) {
      found.push(narrowBoundary(node, lo, hi, before !== null));
    } else if (
      before !== null &&
      now !== null &&
      before * now < 0 &&
      Math.max(Math.abs(before), Math.abs(now)) > towering
    ) {
      found.push(narrowPole(node, lo, hi));
    }
  }

  /*
   * Merged, because one pole shows itself twice: the curve leaves on one side and returns on the
   * other, so a definedness scan reports two boundaries a hair apart. Two dashed lines drawn on
   * top of each other is a thicker line, and the list is also what a caller counts.
   */
  const merged: number[] = [];
  for (const x of found.map(tidy).sort((a, b) => a - b)) {
    const last = merged[merged.length - 1];
    if (last === undefined || Math.abs(x - last) > Math.abs(step)) merged.push(x);
  }
  return merged;
}

/**
 * The asymptotes of `y = f(x)` over the interval being drawn.
 *
 * Vertical ones are restricted to the interval — an asymptote off the side of the frame is a
 * dashed line the reader cannot see the point of. Horizontal and oblique ones describe behaviour
 * at infinity and are reported whatever the interval.
 */
export function asymptotesOf(node: Node, from: number, to: number): readonly Asymptote[] {
  const found: Asymptote[] = [];

  for (const x of suspectedPoles(node, from, to, 400)) {
    if (escapesAt(node, x)) found.push({ kind: 'vertical', at: x });
  }

  /*
   * A horizontal asymptote must hold on **both** sides to be drawn as one line.
   *
   * `y = 2^x` tends to 0 going left and to infinity going right, and the line `y = 0` is still
   * the right annotation — so a one-sided limit counts. What must not happen is two different
   * one-sided limits collapsing into a single line: `arctan` approaches `π/2` one way and
   * `−π/2` the other, and drawing one of them would be a figure that misinforms.
   */
  const left = limitAt(node, -1);
  const right = limitAt(node, 1);

  if (left !== null && right !== null) {
    if (Math.abs(left - right) < 1e-6) found.push({ kind: 'horizontal', at: right });
    else found.push({ kind: 'horizontal', at: left }, { kind: 'horizontal', at: right });
  } else if (left !== null) found.push({ kind: 'horizontal', at: left });
  else if (right !== null) found.push({ kind: 'horizontal', at: right });
  else {
    /*
     * No horizontal limit, so the curve may still approach a slanted line — `tiệm cận xiên`,
     * which the 2018 syllabus asks for. The slope is `f(x)/x` far out and the intercept is what
     * is left of `f(x) − slope·x`; both have to settle for the line to be real.
     */
    const slopeNear = evaluateAt(node, FAR);
    const slopeFar = evaluateAt(node, FARTHER);
    if (slopeNear !== null && slopeFar !== null) {
      const a = tidy(slopeFar / FARTHER);
      const consistent = Math.abs(slopeNear / FAR - a) < LIMIT_TOLERANCE;
      const b = tidy(slopeFar - a * FARTHER);
      const bNear = slopeNear - a * FAR;

      if (consistent && a !== 0 && Math.abs(bNear - b) < Math.max(1, Math.abs(b)) * 1e-3) {
        found.push({ kind: 'oblique', at: b, slope: a });
      }
    }
  }

  return found;
}
